import { useEffect } from 'react';

/**
 * Tiny generic confirmation dialog. Centred overlay, narrow width,
 * Cancel + Destructive-confirm buttons. Esc + scrim click cancel.
 *
 * Used by Previous Conversations for delete confirmation. Generic
 * so future destructive actions can reuse it.
 *
 * Reuses the .history-modal-scrim styling for visual consistency
 * with the rest of the dashboard's overlays - only the inner
 * modal is sized differently (max-width: 380 instead of 640).
 */
interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** When true, renders the confirm button in red - for delete
   *  / destructive actions. Default true since this dialog is
   *  primarily used for destructive flows. */
  destructive?: boolean;
  /** Disables both buttons + shows the busy label on confirm.
   *  Set while the underlying mutation is in flight. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  destructive = true,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  // Esc to cancel.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, busy]);

  return (
    <div
      className="history-modal-scrim"
      onClick={() => {
        if (!busy) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="confirm-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="confirm-dialog-title">{title}</h2>
        <p className="confirm-dialog-message">{message}</p>
        <div className="confirm-dialog-actions">
          <button
            type="button"
            className="confirm-dialog-btn confirm-dialog-btn-cancel"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={
              'confirm-dialog-btn ' +
              (destructive
                ? 'confirm-dialog-btn-destructive'
                : 'confirm-dialog-btn-confirm')
            }
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy ? `${confirmLabel}…` : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
